import React, { FunctionComponent, ReactNode } from "react";
import { Button, ButtonPropsColorOverrides, SxProps } from "@mui/material";
import { OverridableStringUnion } from "@mui/types";
import { Link as RouterLink } from "react-router-dom";

/**
 * Interface defining the props for the LinkButton component.
 * @property {ReactNode} startIcon - The icon to display at the start of the button
 * @property {string} label - The text label to display on the button
 * @property {boolean} [disabled] - Optional flag to disable the button
 * @property {string} to - The route path to navigate to when clicked
 * @property {SxProps} [style] - Optional custom styles to apply to the button
 * @property {("button"|"submit"|"reset")} [type] - Optional button type
 * @property {OverridableStringUnion<"secondary"|"inherit"|"primary"|"success"|"error"|"info"|"warning", ButtonPropsColorOverrides>} [color] - Optional button color
 */
interface LinkButtonProps {
  startIcon: ReactNode;
  label: string;
  disabled?: boolean;
  to: string;
  style?: SxProps;
  type?: "button" | "submit" | "reset";
  color?: OverridableStringUnion<
    | "secondary"
    | "inherit"
    | "primary"
    | "success"
    | "error"
    | "info"
    | "warning",
    ButtonPropsColorOverrides
  >;
}

/**
 * A reusable button component that combines Material-UI Button with React Router Link functionality.
 * 
 * This component creates a styled button that navigates to a specified route when clicked.
 * It supports various customization options including icons, colors, and styles.
 * 
 * @component
 * @param {LinkButtonProps} props - The props passed to the component
 * @param {ReactNode} props.startIcon - The icon to display at the start of the button
 * @param {string} props.label - The text label to display on the button
 * @param {boolean} [props.disabled=false] - Whether the button is disabled
 * @param {string} props.to - The route path to navigate to
 * @param {SxProps} [props.style={}] - Custom styles to apply to the button
 * @param {("button"|"submit"|"reset")} [props.type] - The button type
 * @param {string} [props.color="secondary"] - The button color
 * 
 * @example
 * ```tsx
 * <LinkButton
 *   startIcon={<AddIcon />}
 *   label="Create New"
 *   to="/create"
 *   color="primary"
 * />
 * ```
 * 
 * @remarks
 * - Uses Material-UI Button component
 * - Integrates with React Router for navigation
 * - Supports Material-UI theme customization
 * - Provides consistent styling across the application
 * - Handles disabled states
 * 
 * @returns {JSX.Element} A React component that renders a styled navigation button
 */
const LinkButton: FunctionComponent<LinkButtonProps> = (props) => {
  const {
    startIcon,
    label,
    color,
    type,
    to,
    style = {},
    disabled = false,
  } = props;
  return (
    <>
      <Button
        startIcon={startIcon}
        color={color ? color : "secondary"}
        variant="contained"
        size="small"
        type={type}
        sx={{
          fontSize: "1rem",
          textTransform: "none",
          ...style,
        }}
        component={RouterLink}
        to={to}
        disabled={disabled}
      >
        {label}
      </Button>
    </>
  );
};

export default LinkButton;
